import { Box, Flex, Stack, Tag, TagLabel, Text, VStack } from '@chakra-ui/react';
import { FormattedMessage } from 'react-intl';
import { useMemo } from 'react';
import { EventType } from 'api/responseTypes';
import { CardPalette, HistoryCardProps } from './HistoryCard.types';
import { CARD_PALETTES } from './HistoryCard.styles';
import { ConcertImage, MatchDisplay } from './components';

export const HistoryCard = ({ historyElement }: HistoryCardProps) => {
  const { tickets, event, orderNumber, totalPrice, currency, ticketOrderCreatedAt } =
    historyElement;

  const normalizedEventType = useMemo(() => {
    const rawEventType = event.eventType as unknown;
    if (typeof rawEventType === 'string') return rawEventType.toUpperCase();
    if (typeof rawEventType === 'number') {
      const enumValue = EventType[rawEventType];
      if (typeof enumValue === 'string') return enumValue.toUpperCase();
    }
    return '';
  }, [event.eventType]);

  const isConcert = normalizedEventType === 'CONCERT';
  const isMatch = normalizedEventType === 'SPORT';

  const eventTypeMessageId = useMemo(() => {
    if (isConcert) return 'history.eventType.concert';
    if (isMatch) return 'history.eventType.match';
    return 'history.eventType.generic';
  }, [isConcert, isMatch]);

  const formatAmount = (amount?: number) => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return '--';
    return new Intl.NumberFormat('en-US').format(amount);
  };

  const formatOrderDate = (dateString: string | Date) => {
    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(date.getTime())) return '';
      return new Intl.DateTimeFormat('ar', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    } catch {
      return '';
    }
  };

  const cardPalette = useMemo<CardPalette>(() => {
    if (isMatch) return CARD_PALETTES.match;
    if (isConcert) return CARD_PALETTES.concert;
    return CARD_PALETTES.generic;
  }, [isConcert, isMatch]);

  const formattedPurchaseDate = ticketOrderCreatedAt ? formatOrderDate(ticketOrderCreatedAt) : '';

  const getSoftColor = (color?: string, alpha = 0.15) => {
    if (!color) return `rgba(12, 113, 97, ${alpha})`;
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const expanded =
        hex.length === 3
          ? hex
              .split('')
              .map((ch) => ch + ch)
              .join('')
          : hex;
      const bigint = Number.parseInt(expanded, 16);
      const r = (bigint >> 16) & 255;
      const g = (bigint >> 8) & 255;
      const b = bigint & 255;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (color.startsWith('rgba')) {
      const [r, g, b] = color
        .replace('rgba', '')
        .replace('(', '')
        .replace(')', '')
        .split(',')
        .slice(0, 3)
        .map((value) => Number(value.trim()));
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    if (color.startsWith('rgb')) {
      const [r, g, b] = color
        .replace('rgb', '')
        .replace('(', '')
        .replace(')', '')
        .split(',')
        .map((value) => Number(value.trim()));
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgba(12, 113, 97, ${alpha})`;
  };

  const renderEventVisual = () => {
    if (isMatch && event.match?.teamA && event.match?.teamB) {
      return (
        <Flex justify='center' w='100%'>
          <MatchDisplay teamA={event.match.teamA} teamB={event.match.teamB} palette={cardPalette} />
        </Flex>
      );
    }
    if (event.concert?.imageLink) {
      return (
        <Flex justify='center' w='100%'>
          <ConcertImage
            imageLink={event.concert.imageLink}
            title={event.concert.title}
            palette={cardPalette}
          />
        </Flex>
      );
    }
    return null;
  };

  const ShortDivider = () => <Box h='1px' w='60%' bg='dark.D200' mx='auto' borderRadius='full' />;

  const LongDivider = () => <Box h='1px' w='100%' bg='dark.D200' borderRadius='full' />;

  const renderTicketCard = (ticket: (typeof tickets)[0]) => {
    const categoryLabel = ticket.category?.label;
    const categoryColor = ticket.category?.color;
    const hasSeatDetails = ticket.section || ticket.row || ticket.seatNumber;
    const hasSector = ticket.sector && ticket.sector !== ticket.section;
    const ticketBackground = getSoftColor(categoryColor, 0.22);

    return (
      <Box
        key={ticket.id}
        bg={ticketBackground || 'rgba(12, 113, 97, 0.12)'}
        borderRadius={{ base: '2xl', md: '3xl' }}
        p={{ base: 4, md: 5 }}
        boxShadow='0 18px 46px rgba(15, 23, 42, 0.12)'
        w='100%'
      >
        <VStack align='stretch' spacing={{ base: 3, md: 4 }} w='100%'>
          <Box>
            <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
              <FormattedMessage id='history.ticket.category' defaultMessage='Ticket type' />
            </Text>
            {categoryLabel ? (
              <Tag
                bg='rgba(255, 255, 255, 0.85)'
                color={categoryColor || 'dark.D900'}
                borderRadius='full'
                px={4}
                py={1.5}
                fontWeight='600'
              >
                <TagLabel>{categoryLabel}</TagLabel>
              </Tag>
            ) : ticket.sector ? (
              <Tag
                bg='rgba(255, 255, 255, 0.85)'
                color='dark.D900'
                borderRadius='full'
                px={4}
                py={1.5}
                fontWeight='600'
              >
                <TagLabel>{ticket.sector}</TagLabel>
              </Tag>
            ) : null}
          </Box>

          <Box>
            <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
              <FormattedMessage id='history.ticket.price' defaultMessage='Ticket price' />
            </Text>
            <Flex align='baseline' gap={1}>
              <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight='800' color='dark.D900'>
                {formatAmount(ticket.price)}
              </Text>
              <Text fontSize='sm' color='dark.D600' fontWeight='500'>
                {ticket.currency || currency}
              </Text>
            </Flex>
          </Box>

          {hasSeatDetails && (
            <Box bg='rgba(255, 255, 255, 0.7)' borderRadius='xl' p={{ base: 3, md: 4 }}>
              <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={2}>
                <FormattedMessage id='history.ticket.seatDetails' defaultMessage='Seat details' />
              </Text>
              <Flex
                direction={{ base: 'column', sm: 'row' }}
                gap={{ base: 2, sm: 4 }}
                flexWrap='wrap'
              >
                {ticket.section && (
                  <Tag
                    bg='white'
                    color='dark.D900'
                    borderRadius='lg'
                    px={3}
                    py={1}
                    fontWeight='600'
                  >
                    <TagLabel>
                      <FormattedMessage id='history.ticket.sectionLabel' defaultMessage='Section' />
                      : {ticket.section}
                    </TagLabel>
                  </Tag>
                )}
                {ticket.row && (
                  <Tag
                    bg='white'
                    color='dark.D900'
                    borderRadius='lg'
                    px={3}
                    py={1}
                    fontWeight='600'
                  >
                    <TagLabel>
                      <FormattedMessage id='history.ticket.rowLabel' defaultMessage='Row' />:{' '}
                      {ticket.row}
                    </TagLabel>
                  </Tag>
                )}
                {ticket.seatNumber && (
                  <Tag
                    bg='white'
                    color='dark.D900'
                    borderRadius='lg'
                    px={3}
                    py={1}
                    fontWeight='600'
                  >
                    <TagLabel>
                      <FormattedMessage
                        id='history.ticket.seatNumberLabel'
                        defaultMessage='Seat number'
                      />
                      : {ticket.seatNumber}
                    </TagLabel>
                  </Tag>
                )}
                {hasSector && (
                  <Tag
                    bg='white'
                    color='dark.D900'
                    borderRadius='lg'
                    px={3}
                    py={1}
                    fontWeight='600'
                  >
                    <TagLabel>
                      <FormattedMessage id='history.ticket.sectorLabel' defaultMessage='Sector' />:{' '}
                      {ticket.sector}
                    </TagLabel>
                  </Tag>
                )}
              </Flex>
            </Box>
          )}

          {ticket.holderName && (
            <Box>
              <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                <FormattedMessage id='history.ticket.holder' defaultMessage='Ticket holder' />
              </Text>
              <VStack align='flex-start' spacing={0.5}>
                <Text fontSize='md' fontWeight='700' color='dark.D900'>
                  {ticket.holderName}
                </Text>
                {ticket.holderPhone && (
                  <Box>
                    <Text fontSize='xs' color='dark.D500' fontWeight='600' mt={2} mb={0.5}>
                      <FormattedMessage
                        id='history.ticket.phone'
                        defaultMessage='Ticket holder phone'
                      />
                    </Text>
                    <Text fontSize='sm' color='dark.D600' fontWeight='500'>
                      {ticket.holderPhone}
                    </Text>
                  </Box>
                )}
              </VStack>
            </Box>
          )}

          <VStack align='flex-start' spacing={1}>
            <Text fontSize='xs' color='dark.D500' fontWeight='600'>
              <FormattedMessage id='history.ticket.status.label' defaultMessage='Ticket status' />
            </Text>
            <Tag
              size='lg'
              bg={ticket.used ? 'rgba(248, 113, 113, 0.15)' : 'rgba(34, 197, 94, 0.15)'}
              color={ticket.used ? 'red.700' : 'green.700'}
              borderRadius='full'
              px={4}
              py={1.5}
              fontWeight='600'
            >
              <TagLabel fontSize='sm'>
                <FormattedMessage
                  id={ticket.used ? 'history.ticket.status.used' : 'history.ticket.status.unused'}
                  defaultMessage={ticket.used ? 'Used' : 'Unused'}
                />
              </TagLabel>
            </Tag>
          </VStack>
        </VStack>
      </Box>
    );
  };

  return (
    <Box
      as='li'
      position='relative'
      bg='linear-gradient(135deg, rgba(255,255,255,0.95), rgba(245,248,247,0.96))'
      borderRadius={{ base: '2xl', md: '3xl' }}
      listStyleType='none'
      boxShadow='0 40px 120px rgba(15, 23, 42, 0.12)'
      overflow='hidden'
      transition='transform 0.3s ease, box-shadow 0.3s ease'
      _hover={{
        transform: 'translateY(-8px)',
        boxShadow: '0 50px 140px rgba(15, 23, 42, 0.16)',
      }}
      width='100%'
      dir='rtl'
    >
      {/* Main content */}
      <Stack
        spacing={{ base: 4, md: 5 }}
        p={{ base: 4, md: 6 }}
        position='relative'
        zIndex={1}
        align='stretch'
        w='100%'
      >
        <VStack
          as='section'
          align='stretch'
          spacing={{ base: 3, md: 4 }}
          bg='rgba(255, 255, 255, 0.92)'
          borderRadius='2xl'
          p={{ base: 4, md: 5 }}
          boxShadow='0 12px 50px rgba(15, 23, 42, 0.12)'
        >
          <Text fontSize='sm' fontWeight='700' color='dark.D800'>
            <FormattedMessage id='history.orderDetails.heading' defaultMessage='Order details' />
          </Text>
          <LongDivider />
          <Stack direction={{ base: 'column', md: 'row' }} spacing={{ base: 3, md: 6 }}>
            <Box>
              <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                <FormattedMessage id='history.orderNumber' defaultMessage='Order number' />
              </Text>
              <Text fontSize='xl' fontWeight='800' color='dark.D900'>
                #{orderNumber}
              </Text>
            </Box>
            <Box>
              <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                <FormattedMessage
                  id='history.orderDetails.purchaseDate'
                  defaultMessage='Purchase date'
                />
              </Text>
              <Text fontSize='md' fontWeight='600' color='dark.D900'>
                {formattedPurchaseDate || '--'}
              </Text>
            </Box>
          </Stack>
          <ShortDivider />
          <Tag
            size={{ base: 'lg', md: 'xl' }}
            bg={getSoftColor(cardPalette.accentColor, 0.2)}
            color={cardPalette.accentColor}
            borderRadius='full'
            px={{ base: 4, md: 6 }}
            py={{ base: 2, md: 3 }}
            fontWeight='bold'
            alignSelf='flex-start'
          >
            <TagLabel
              textTransform='uppercase'
              fontSize={{ base: 'sm', md: 'md' }}
              letterSpacing='wide'
            >
              <FormattedMessage
                id={eventTypeMessageId}
                defaultMessage={isConcert ? 'حدث' : isMatch ? 'مباراة' : 'حدث'}
              />
            </TagLabel>
          </Tag>
        </VStack>

        <VStack
          as='section'
          align='stretch'
          spacing={{ base: 3, md: 4 }}
          bg='rgba(255, 255, 255, 0.92)'
          borderRadius='2xl'
          p={{ base: 4, md: 5 }}
          boxShadow='0 12px 50px rgba(15, 23, 42, 0.12)'
        >
          <Text fontSize='sm' fontWeight='700' color='dark.D800'>
            <FormattedMessage id='history.eventDetails.heading' defaultMessage='Event details' />
          </Text>
          <LongDivider />
          {(event.concert?.title || event.match?.title) && (
            <Box>
              <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                <FormattedMessage id='history.event.titleLabel' defaultMessage='Event title' />
              </Text>
              <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight='700' color='dark.D900'>
                {event.concert?.title || event.match?.title}
              </Text>
              {(event.concert?.subTitle || event.match?.subTitle) && (
                <Box mt={2}>
                  <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                    <FormattedMessage id='history.event.subtitleLabel' defaultMessage='Subtitle' />
                  </Text>
                  <Text fontSize='sm' color='dark.D600'>
                    {event.concert?.subTitle || event.match?.subTitle}
                  </Text>
                </Box>
              )}
            </Box>
          )}
          <ShortDivider />
          <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 3, md: 6 }} flexWrap='wrap'>
            {event.date && (
              <Box>
                <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                  <FormattedMessage id='history.date' defaultMessage='Date' />
                </Text>
                <Text fontSize='md' fontWeight='600' color='dark.D900'>
                  {event.date}
                </Text>
              </Box>
            )}
            {event.time && (
              <Box>
                <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                  <FormattedMessage id='history.time' defaultMessage='Time' />
                </Text>
                <Text fontSize='md' fontWeight='600' color='dark.D900'>
                  {event.time}
                </Text>
              </Box>
            )}
            {event.stadium?.name && (
              <Box>
                <Text fontSize='xs' color='dark.D500' fontWeight='600' mb={1}>
                  <FormattedMessage id='history.event.stadium' defaultMessage='Stadium' />
                </Text>
                <Text fontSize='md' fontWeight='600' color='dark.D900'>
                  {event.stadium.name}
                </Text>
              </Box>
            )}
          </Flex>
          <ShortDivider />
          {renderEventVisual()}
        </VStack>

        <VStack
          as='section'
          align='stretch'
          spacing={{ base: 3, md: 4 }}
          bg='rgba(255, 255, 255, 0.92)'
          borderRadius='2xl'
          p={{ base: 4, md: 5 }}
          boxShadow='0 12px 50px rgba(15, 23, 42, 0.12)'
        >
          <Text fontSize='sm' fontWeight='700' color='dark.D800'>
            <FormattedMessage id='history.ticketDetails.heading' defaultMessage='Ticket details' />{' '}
            ({tickets.length})
          </Text>
          <LongDivider />
          <Stack spacing={{ base: 3, md: 4 }} w='100%'>
            {tickets.map((ticket) => renderTicketCard(ticket))}
          </Stack>
        </VStack>
      </Stack>

      {/* Footer */}
      <Box bg='white' borderTop='1px solid' borderColor='dark.D50'>
        <Flex
          justify='space-between'
          align='center'
          px={{ base: 4, md: 6 }}
          py={{ base: 3, md: 4 }}
          flexWrap='wrap'
          gap={{ base: 2, md: 3 }}
          w='100%'
        >
          <VStack align='flex-end' spacing={{ base: 0.5, md: 1 }}>
            <Text
              fontSize={{ base: 'xs', md: 'sm' }}
              color='dark.D400'
              textTransform='uppercase'
              fontWeight='600'
              letterSpacing='0.2em'
            >
              المبلغ المدفوع
            </Text>
            <Flex align='baseline' gap={{ base: 1, md: 2 }}>
              <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight='800' color='dark.D900'>
                {formatAmount(totalPrice)}
              </Text>
              <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight='600' color='dark.D500'>
                {currency || tickets[0]?.currency || 'IQD'}
              </Text>
            </Flex>
          </VStack>
          <Text fontSize='sm' color='dark.D500' fontWeight='500'>
            {tickets.length} تذكرة
          </Text>
        </Flex>
      </Box>
    </Box>
  );
};
